import AppContext, { IAppContext } from 'App/AppContext';
import { ProjectSummary } from 'models/projects/project';
import { ProjectState } from 'models/projects/projectEnums';
import { useContext, useEffect, useState } from 'react';
import { apiGetProjects } from 'services/Api/projectService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { getLocalStorageData, LocalStorageKeys } from 'utils/localstorage';
import { isProjectsModuleEnabledForTenant } from 'utils/projects';
import { globalCustomerId, globalOrgUnitId } from 'globalState';

/** Project ids belong to one request scope, not to the signed-in user across tenant switches. */
export const getProjectScopeKey = (appContext: IAppContext): string =>
  [appContext.user.tenant.id, appContext.user.login.tenantId, globalOrgUnitId, globalCustomerId].join(':');

/**
 * Active projects for the tenant, newest first (list endpoint; no milestones).
 * Skips fetch when Projects module or feature is off to avoid 403 on every page.
 */
export const useActiveProjects = (): { activeProjects: ProjectSummary[]; isLoading: boolean } => {
  const appContext = useContext(AppContext);
  const scopeKey = getProjectScopeKey(appContext);
  const [loaded, setLoaded] = useState<{ scopeKey: string; activeProjects: ProjectSummary[] } | undefined>();
  const canLoad =
    appContext.isAuthenticated &&
    !appContext.isAuthInProgress &&
    isProjectsModuleEnabledForTenant(appContext.globalDataCache.settings) &&
    hasUserFeature(appContext, FeatureTypes.Projects);

  useEffect(() => {
    if (!canLoad) {
      setLoaded(undefined);

      return;
    }

    let mounted = true;

    const loadData = async () => {
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const projects = await apiGetProjects(accessToken);
        if (!mounted) return;

        const active = projects
          .filter((project) => project.state === ProjectState.active)
          .sort((a, b) => (b.created?.getTime() ?? 0) - (a.created?.getTime() ?? 0));

        setLoaded({ scopeKey, activeProjects: active });
      } catch (err) {
        if (!mounted) return;
        appContext.setError(err);
        setLoaded({ scopeKey, activeProjects: [] });
      }
    };

    void loadData();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoad, scopeKey]);

  const current = canLoad && loaded?.scopeKey === scopeKey ? loaded : undefined;

  return { activeProjects: current?.activeProjects ?? [], isLoading: canLoad && current === undefined };
};

/** Last-opened active project, else newest active; same rule as ProjectEntry. */
export const getResumableProject = (activeProjects: ProjectSummary[], lastOpenedId: number): ProjectSummary | undefined => {
  return activeProjects.find((project) => project.projectId === lastOpenedId) ?? activeProjects[0];
};

export const getLastOpenedProjectId = (appContext: IAppContext): number => {
  return Number(getLocalStorageData(appContext, LocalStorageKeys.ProjectLastOpened));
};
